import { useState, useEffect, useCallback } from 'react'
import {
  getLastViewedPid,
  getLastViewedPedigree,
  getTreeStartingPid,
  setLastViewedPedigree,
  setLastViewedPid,
  type useGroupsContext,
} from '@fs/zion-tree-groups'
import { type PID } from '@fs/zion-tree-types'
import { type PedigreeViewType } from '../../PedigreeRoutes'

type GetPedigreeAndPidArgs = {
  treeId?: string
}

type GetPedigreeAndPidReturn = {
  view: PedigreeViewType
  pid: PID
  startingPid?: PID
}

export const getPedigreeAndPid = async ({ treeId }: GetPedigreeAndPidArgs): Promise<GetPedigreeAndPidReturn> => {
  const lastViewedPedigree = await getLastViewedPedigree()
  const lastViewedPidAndTree = await getLastViewedPid('pedigree')
  // @ts-expect-error -- update later so we keep history for file
  const [lastPid, lastPidTreeId] = lastViewedPidAndTree?.split(':') || []
  const lastViewedPid = treeId === lastPidTreeId ? lastPid : null
  const view = lastViewedPedigree || 'portrait'

  const { personId: startingPid } = (await getTreeStartingPid(treeId || 'PRIVATE')) || {}
  const pid = lastViewedPid || startingPid || ''
  // @ts-expect-error -- update later so we keep history for file
  return { view, pid, startingPid }
}

type UsePedigreeDataArgs = {
  treeId?: ReturnType<typeof useGroupsContext>['currentTreeId']
  groupsLoading: ReturnType<typeof useGroupsContext>['loading']
}

type UsePedigreeDataReturn = {
  lastView?: PedigreeViewType
  lastPid?: PID
  startingPid?: PID
  loading: boolean
  updateLastView: (view: PedigreeViewType) => void
  updateLastPid: (pid: PID) => void
}
export const usePedigreeData = ({ treeId, groupsLoading }: UsePedigreeDataArgs): UsePedigreeDataReturn => {
  const [startingData, setStartingData] = useState<Omit<UsePedigreeDataReturn, 'updateLastView' | 'updateLastPid'>>({
    loading: true,
  })

  const updateLastView = useCallback((view) => {
    setLastViewedPedigree(view)
    setStartingData((prev) => ({ ...prev, lastView: view }))
  }, [])

  const updateLastPid = useCallback(
    (pid) => {
      setLastViewedPid(`${pid}:${treeId}`, 'pedigree')
      setStartingData((prev) => ({ ...prev, lastPid: pid }))
    },
    [treeId]
  )

  useEffect(() => {
    let mounted = true

    if (!groupsLoading) {
      // Get fallback values for pid and view if not contained in the URL
      getPedigreeAndPid({ treeId })
        .then(({ view, pid, startingPid }) => {
          return mounted && setStartingData({ lastView: view, lastPid: pid, startingPid, loading: false })
        })
        .catch(() => undefined)
    }

    return () => {
      mounted = false
    }
  }, [groupsLoading, treeId])
  return { ...startingData, updateLastView, updateLastPid }
}
